'use client';

import { Open, Swap } from '@drift-labs/icons';
import { SpotBalanceType, SpotMarketConfig } from '@drift-labs/sdk';
import {
	matchEnum,
	UIMarket,
	UISerializableDepositRecord,
} from '@drift/common';
import { useEffect, useMemo, useState } from 'react';
import useDateDisplayState from 'src/hooks/useDateDisplayState';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { HistoryTableUIProps } from 'src/hooks/historyData/historyDataTypes';
import { useRewardsHistory } from 'src/hooks/historyData/useRewardsHistory';
import useHistoryTableFilterStore from '../../stores/historyTableFilterStore/useHistoryTableFilterStore';
import { sortRecordsByTs } from 'src/utils/events';
import { getUrlForTx } from 'src/utils/explorer';
import TableV2 from '../Tables/TableV2';
import ToggleHeaderCell from 'src/components/Tables/cells/ToggleHeaderCell';
import Link from 'src/components/primitives/Link/Link';
import MobileTableCard from 'src/components/Tables/cards/MobileTableCard';
import MarketIcon from 'src/components/Utils/MarketIcon';
import NumberDisplay from 'src/components/NumberDisplay/NumberDisplay';
import IconButton from 'src/components/primitives/IconButton/IconButton';
import Button from 'src/components/primitives/Button/Button';
import HistoryTable from 'src/components/HistoryTables/HistoryTable';

const PAGE_SIZE = 20;
const EMPTY_REWARDS_ARRAY: UISerializableDepositRecord[] = [];
const TABLE_GRID = '1fr 1fr 1fr 1fr 50px';
const HEADERS = ['Time', 'Market', 'Amount', 'Product', ''];

const V2RewardHistoryTableDisplay = (
	props: HistoryTableUIProps<UISerializableDepositRecord>
) => {
	const filters = useHistoryTableFilterStore((s) =>
		s.getFilterState('rewards')
	);
	const currentUiPage = filters.currentUiPage;
	const isMobileScreenSize = useIsMobileScreenSize();
	const { displayDatesAsTimestamp, toggleDateDisplayType } =
		useDateDisplayState();

	// Use history data from hook instead of Zustand store
	const { historyData } = props;

	const fullRewards: UISerializableDepositRecord[] =
		historyData.initialHistoryLoaded
			? historyData.records
			: EMPTY_REWARDS_ARRAY;
	const isLoading = historyData.loading ?? false;

	const filteredRewards = useMemo(
		() =>
			props.filterFn
				? fullRewards?.filter((record) => props.filterFn(record, filters))
				: fullRewards,
		[fullRewards, props.filterFn, filters]
	);

	const [currentRewardHistory, setCurrentRewardHistory] = useState(
		filteredRewards?.slice(0, PAGE_SIZE) ?? undefined
	);

	const maxNumOfRecordsToDisplay =
		filters.getFilterMode() === 'SHORT_TERM'
			? (historyData.totalCount ?? 0) // short term data API returns pages of 20 records up to the current page, and we do a workaround to (+1 to total count) when we know there is a next page
			: (filteredRewards?.length ?? 0); // long term data API returns up to 5000 records for the first page, so we check the actual displayed records (filtered records) length

	// extract and set the current page records
	useEffect(() => {
		setCurrentRewardHistory(
			filteredRewards?.slice(
				currentUiPage * PAGE_SIZE,
				(currentUiPage + 1) * PAGE_SIZE
			) ?? undefined
		);
	}, [filteredRewards, currentUiPage]);

	return (
		<HistoryTable
			tableGrid={TABLE_GRID}
			pageSize={PAGE_SIZE}
			currentPage={currentUiPage}
			pageChangeCallback={props.executePageChange}
			totalItems={maxNumOfRecordsToDisplay}
			loading={isLoading ?? false}
			tableState={{
				records: filteredRewards,
				id: 'reward_history_table',
			}}
			tableContentTop={
				<TableV2.HeaderRow grid={TABLE_GRID} lastColumnJustify="start">
					{HEADERS.map((label, index) => (
						<TableV2.HeaderCell key={`deposit_header_${index}`}>
							{label.toLowerCase() === 'time' ? (
								<ToggleHeaderCell
									label={label}
									onClick={toggleDateDisplayType}
								/>
							) : (
								label
							)}
						</TableV2.HeaderCell>
					))}
				</TableV2.HeaderRow>
			}
			tableContentMiddle={sortRecordsByTs(currentRewardHistory)?.map(
				(record, index) => {
					const key = `${index}_${record.ts}`;
					const product = `Borrow/Lend ${
						(
							UIMarket.createSpotMarket(record.marketIndex)
								.market as SpotMarketConfig
						).poolId > 0
							? '(Isolated Pool)'
							: '(Main Pool)'
					}`;

					if (isMobileScreenSize) {
						return (
							<MobileTableCard.Root key={key} className="gap-y-lg">
								<div className="flex items-center gap-x-xs">
									<MarketIcon
										marketSymbol={
											UIMarket.createSpotMarket(record.marketIndex).uiSymbols
												.baseAssetSymbol
										}
										sizeClass="size-5"
									/>
									<span className="typo-t2">
										{
											UIMarket.createSpotMarket(record.marketIndex).uiSymbols
												.baseAssetSymbol
										}
									</span>
								</div>
								<div className="grid w-full grid-cols-2">
									<MobileTableCard.LabelWithValue
										label="Amount"
										value={
											<NumberDisplay
												value={record.amount}
												type="number_signed"
												suffix={
													UIMarket.createSpotMarket(record.marketIndex)
														.uiSymbols.baseAssetSymbol
												}
												decimals={4}
												isAccountValueToHide
												signDependentColor
												trimZeroes
											/>
										}
									/>
									<MobileTableCard.LabelWithValue
										className="items-end"
										label="Product"
										value={product}
									/>
								</div>
								<div className="flex items-center gap-x-xs">
									<MobileTableCard.Label>Time</MobileTableCard.Label>
									<IconButton
										size="sm"
										variant="tertiary"
										className="-m-md p-md"
										onClick={toggleDateDisplayType}
										icon={Swap}
									/>
									<TableV2.DateCell
										className="w-fit py-0 whitespace-nowrap"
										displayAsTimestamp={displayDatesAsTimestamp}
										date={new Date(record.ts.toNumber() * 1000)}
									/>
								</div>
								<Button
									size="sm"
									variant="secondary"
									as="a"
									href={getUrlForTx(record.txSig)}
									openInNewTab
								>
									View in scanner
								</Button>
							</MobileTableCard.Root>
						);
					}

					return (
						<TableV2.BodyRow
							key={key}
							grid={TABLE_GRID}
							lastColumnJustify="start"
							className="h-10 border-0"
							isDataRow
						>
							<TableV2.DateCell
								key={`size_${index}`}
								date={new Date(record.ts.toNumber() * 1000)}
								displayAsTimestamp={displayDatesAsTimestamp}
							/>

							<TableV2.MarketCell
								key={`market_${index}`}
								marketSymbol={
									UIMarket.createSpotMarket(record.marketIndex).uiSymbols
										.baseAssetSymbol
								}
								className="items-center"
							/>

							<TableV2.AssetCell
								key={`base_amount_${index}`}
								value={
									matchEnum(record.direction, SpotBalanceType.DEPOSIT)
										? record.amount
										: record.amount.neg()
								}
								suffix={
									UIMarket.createSpotMarket(record.marketIndex).uiSymbols
										.baseAssetSymbol
								}
								decimals={4}
								disableTooltip
								isAccountValueToHide
								trimZeroes
								semantic
							/>

							<TableV2.BodyCell key={`product_${index}`}>
								{product}
							</TableV2.BodyCell>

							<TableV2.BodyCell className="justify-end">
								<Link
									variant="secondary"
									href={getUrlForTx(record.txSig)}
									rightIcon="none"
									openInNewTab
								>
									<Open size={16} />
								</Link>
							</TableV2.BodyCell>
						</TableV2.BodyRow>
					);
				}
			)}
		/>
	);
};

const V2RewardHistoryTable = () => {
	const tableState = useRewardsHistory();

	return <V2RewardHistoryTableDisplay {...tableState} />;
};

export default V2RewardHistoryTable;
